
import React from 'react';
import { useState, useEffect } from 'react';
import { StudioImage } from '../types';
import { getStudioApiKey } from '../utils/audio';

interface ImageGalleryProps {
  ui: any;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ ui }) => {
  const [images, setImages] = useState<StudioImage[]>([]);
  const hasKey = !!getStudioApiKey();

  useEffect(() => {
    const saved = localStorage.getItem('groovefx_gallery');
    if (saved) setImages(JSON.parse(saved));
  }, []);

  return (
    <div className="h-full space-y-12 animate-in fade-in duration-700 text-left">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-white/10 pb-10 gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-groove-accent/10 border border-groove-accent/20 text-[9px] font-black text-groove-accent uppercase tracking-[0.3em] mb-4">
            <i className="fa-solid fa-image"></i>
            Visual Archive
          </div>
          <h2 className="text-5xl font-black text-white tracking-tighter uppercase leading-none">{ui.gallery.title}</h2>
          
          <div className={`mt-6 inline-flex items-center gap-4 px-5 py-2 rounded-full text-[9px] font-black uppercase tracking-[0.2em] border ${hasKey ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
            <i className={`fa-solid ${hasKey ? 'fa-link' : 'fa-link-slash'}`}></i>
            Protocol Handshake: {hasKey ? 'Authorized' : 'Missing'}
          </div>
        </div>
        <div className="text-left md:text-right">
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Total Renders</p>
          <p className="text-3xl font-black text-white">{images.length}</p>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-40 space-y-8 bg-white/5 rounded-[4rem] border border-dashed border-white/10">
          <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center">
            <i className="fa-solid fa-mountain-sun text-gray-600 text-4xl"></i>
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-black text-white uppercase tracking-tighter">The Canvas is Empty</h3>
            <p className="text-gray-500 max-w-sm mx-auto">Generate images in the Visual Studio using your Gemini key to see them archived here.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {images.map(img => (
            <div key={img.id} className="group relative glass-card rounded-[2.5rem] overflow-hidden border border-white/10 hover:border-groove-accent/50 transition-all shadow-2xl">
              <img src={img.url} alt={img.prompt} className="w-full aspect-square object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-8">
                 <p className="text-xs text-white font-medium line-clamp-2 mb-4 italic">"{img.prompt}"</p>
                 <div className="flex justify-between items-center border-t border-white/20 pt-4">
                    <span className="text-[9px] font-black uppercase text-groove-accent tracking-widest">{new Date(img.timestamp).toLocaleDateString()}</span>
                    <button className="text-white hover:text-groove-accent transition-colors">
                       <i className="fa-solid fa-download"></i>
                    </button>
                 </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
